/**
 * Recipes page — admin and user management of reusable multi-ingredient recipes.
 *
 * Layout mirrors MealTemplates.tsx:
 *  - Header card list with create / edit / delete actions
 *  - RecipeFormDialog: searchable ingredient editor, live nutrition preview,
 *    weight-override toggle, visibility toggle (public/private)
 */

import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  ChefHat,
  Globe,
  Lock,
  X,
  FlameKindling,
  Tag,
  Eye,
  User,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useAuth } from "@/_core/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { RecipeFormDialog } from "./components/RecipeFormDialog";
import { Link } from "wouter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DraftIngredient {
  /** undefined = not yet saved to DB (new item in dialog) */
  id?: number;
  potravinaId: number;
  nazov: string;
  hmotnostG: string;
  /** kcal per 100g of this food */
  energiaKcal100: string;
}

// ---------------------------------------------------------------------------
// Nutrition helpers
// ---------------------------------------------------------------------------

function computeRecipeNutrition(
  items: DraftIngredient[],
  overrideHmotnost: string
): {
  sumG: number;
  effectiveG: number;
  kcalTotal: number;
  kcalPer100g: number;
} {
  let sumG = 0;
  let kcalTotal = 0;
  for (const item of items) {
    const w = parseFloat(item.hmotnostG) || 0;
    sumG += w;
    kcalTotal += (parseFloat(item.energiaKcal100) || 0) * (w / 100);
  }
  const effectiveG = overrideHmotnost
    ? parseFloat(overrideHmotnost) || sumG
    : sumG;
  const kcalPer100g = effectiveG > 0 ? (kcalTotal / effectiveG) * 100 : 0;
  return { sumG, effectiveG, kcalTotal, kcalPer100g };
}

// ---------------------------------------------------------------------------
// RecipeFormDialog
// ---------------------------------------------------------------------------

interface RecipeFormDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editRecipe?: {
    id: number;
    nazov: string;
    jeVerejny: boolean;
    celkovaHmotnostG: string | null;
    poznamka: string | null;
    items: DraftIngredient[];
    tagIds?: number[];
  } | null;
  potraviny: Array<{
    id: number;
    nazov: string;
    energiaKcal: string | null;
    energiaKj: string | null;
  }>;
  allTags: { id: number; nazov: string; farba: string | null }[];
  canManageGlobalRecipes: boolean;
  canManageTags: boolean;
  onSaved: () => void;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function RecipesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { canWriteOwnRecipes, canManageGlobalRecipes, canManageTags } =
    usePermissions();
  const currentClientId = user?.clientId ?? null;
  const canCreateRecipes = canWriteOwnRecipes || canManageGlobalRecipes;

  const [formOpen, setFormOpen] = useState(false);
  const [editRecipe, setEditRecipe] =
    useState<RecipeFormDialogProps["editRecipe"]>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const utils = trpc.useUtils();
  const { data: recepty = [], isLoading } = trpc.recept.list.useQuery({});
  const { data: potravinyRaw = [] } = trpc.potravina.list.useQuery();
  const { data: allTags = [] } = trpc.tag.list.useQuery();
  const receptIds = recepty.map(r => r.id);
  const { data: receptTagsMap = {} } = trpc.tag.getForReceptList.useQuery(
    { receptIds },
    { enabled: receptIds.length > 0 }
  );

  // Flatten potraviny to the shape RecipeFormDialog needs
  const potraviny = useMemo(
    () =>
      potravinyRaw.map(p => ({
        id: p.id!,
        nazov: p.nazov,
        energiaKcal: p.energiaKcal ?? null,
        energiaKj: p.energiaKj ?? null,
      })),
    [potravinyRaw]
  );

  const deleteMutation = trpc.recept.delete.useMutation();

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteId });
      toast.success(t("mealLibrary:recept.deleted"));
      utils.recept.list.invalidate();
    } catch {
      toast.error(t("mealLibrary:recept.deleteError"));
    } finally {
      setDeleteId(null);
    }
  };

  const toggleTagFilter = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const searchTerms = useMemo(
    () =>
      searchText
        .split(",")
        .map(term => term.trim().toLowerCase())
        .filter(Boolean),
    [searchText]
  );

  const filteredRecepty = useMemo(() => {
    return recepty.filter(r => {
      const searchableParts = [
        r.nazov,
        r.poznamka ?? "",
        ...r.items.map(item => item.potravina.nazov),
      ].map(value => value.toLowerCase());
      const matchesSearch =
        searchTerms.length === 0 ||
        searchTerms.every(term =>
          searchableParts.some(value => value.includes(term))
        );
      const rTags =
        (receptTagsMap as Record<number, { id: number }[]>)[r.id] ?? [];
      const rTagIds = rTags.map(tg => tg.id);
      const matchesTags =
        selectedTagIds.length === 0 ||
        selectedTagIds.every(id => rTagIds.includes(id));
      return matchesSearch && matchesTags;
    });
  }, [recepty, searchTerms, selectedTagIds, receptTagsMap]);

  const canModifyRecipe = (r: (typeof recepty)[0]) =>
    canManageGlobalRecipes ||
    (canWriteOwnRecipes &&
      r.klientId != null &&
      r.klientId === currentClientId);

  const handleEdit = (r: (typeof recepty)[0]) => {
    const rTags =
      (receptTagsMap as Record<number, { id: number }[]>)[r.id] ?? [];
    setEditRecipe({
      id: r.id,
      nazov: r.nazov,
      jeVerejny: r.jeVerejny,
      celkovaHmotnostG: r.celkovaHmotnostG,
      poznamka: r.poznamka,
      tagIds: rTags.map(tg => tg.id),
      items: r.items.map(item => ({
        id: item.id,
        potravinaId: item.potravinaId,
        nazov: item.potravina.nazov,
        hmotnostG: item.hmotnostG,
        energiaKcal100: item.potravina.energiaKcal ?? "0",
      })),
    });
    setFormOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ChefHat className="h-6 w-6" />
              {t("mealLibrary:recept.pageTitle")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t("mealLibrary:recept.pageSubtitle")}
            </p>
          </div>
          {canCreateRecipes && (
            <Button
              onClick={() => {
                setEditRecipe(null);
                setFormOpen(true);
              }}
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              {t("mealLibrary:recept.new")}
            </Button>
          )}
        </div>

        {/* Search + Tag filter bar */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t("mealLibrary:recept.searchPlaceholder")}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchText("")}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <Tag className="h-3.5 w-3.5 text-muted-foreground" />
              {allTags.map(tag => {
                const active = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTagFilter(tag.id)}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:border-primary"
                    }`}
                    style={
                      active && tag.farba
                        ? {
                            backgroundColor: tag.farba,
                            borderColor: tag.farba,
                            color: "#fff",
                          }
                        : tag.farba && !active
                          ? { borderColor: tag.farba, color: tag.farba }
                          : undefined
                    }
                  >
                    {tag.nazov}
                  </button>
                );
              })}
              {selectedTagIds.length > 0 && (
                <button
                  className="text-xs text-muted-foreground underline ml-1"
                  onClick={() => setSelectedTagIds([])}
                >
                  {t("mealLibrary:recept.clearTags")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Recipe list */}
        {isLoading ? (
          <p className="text-muted-foreground">{t("common:loading")}</p>
        ) : filteredRecepty.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {t("mealLibrary:recept.noRecipes")}{" "}
              {canCreateRecipes && (
                <button
                  className="underline"
                  onClick={() => {
                    setEditRecipe(null);
                    setFormOpen(true);
                  }}
                >
                  {t("mealLibrary:recept.createFirst")}
                </button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredRecepty.map(r => {
              const kcalPer100 = parseFloat(r.per100g.energiaKcal).toFixed(1);
              const totalKcal = (
                (parseFloat(r.per100g.energiaKcal) * r.effectiveHmotnostG) /
                100
              ).toFixed(0);
              const rTags =
                (
                  receptTagsMap as Record<
                    number,
                    { id: number; nazov: string; farba: string | null }[]
                  >
                )[r.id] ?? [];
              return (
                <Card key={r.id} className="relative">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base leading-tight">
                          {r.nazov}
                        </CardTitle>
                        {rTags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {rTags.map(tg => (
                              <span
                                key={tg.id}
                                className="px-1.5 py-0 rounded-full text-xs border"
                                style={
                                  tg.farba
                                    ? { borderColor: tg.farba, color: tg.farba }
                                    : undefined
                                }
                              >
                                {tg.nazov}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {r.jeVerejny ? (
                          <Badge variant="secondary" className="gap-1 text-xs">
                            <Globe className="h-3 w-3" />
                            {t("mealLibrary:recept.public")}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <Lock className="h-3 w-3" />
                            {t("mealLibrary:recept.private")}
                          </Badge>
                        )}
                        {r.klientId == null && (
                          <Badge className="text-xs">
                            {t("mealLibrary:recept.global")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardDescription className="text-xs">
                      {t("mealLibrary:recept.totalWeight", {
                        weight: r.effectiveHmotnostG.toFixed(0),
                      })}{" "}
                      ·{" "}
                      <span className="font-semibold text-amber-600">
                        {t("mealLibrary:recept.totalKcal", { kcal: totalKcal })}
                      </span>{" "}
                      ·{" "}
                      <span className="font-semibold text-amber-600">
                        {t("mealLibrary:recept.kcalPer100g", {
                          kcal: kcalPer100,
                        })}
                      </span>
                      {r.klientAlias != null && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-muted-foreground">
                          · <User className="h-3 w-3 inline" /> {r.klientAlias}
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {/* Ingredient badges */}
                    <div className="flex flex-wrap gap-1">
                      {r.items.map(item => (
                        <Badge
                          key={item.id}
                          variant="outline"
                          className="text-xs font-normal"
                        >
                          {item.potravina.nazov}{" "}
                          <span className="text-muted-foreground ml-1">
                            {item.hmotnostG}g
                          </span>
                        </Badge>
                      ))}
                    </div>

                    {/* Macros row */}
                    <div className="grid grid-cols-3 gap-1 text-center text-xs border-t pt-2">
                      <div>
                        <div className="font-semibold text-blue-600">
                          {parseFloat(r.per100g.proteinG).toFixed(1)}g
                        </div>
                        <div className="text-muted-foreground">
                          {t("mealLibrary:recept.protein")}
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold text-orange-600">
                          {parseFloat(r.per100g.tukG).toFixed(1)}g
                        </div>
                        <div className="text-muted-foreground">
                          {t("mealLibrary:recept.fat")}
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold text-teal-600">
                          {parseFloat(r.per100g.sacharidG).toFixed(1)}g
                        </div>
                        <div className="text-muted-foreground">
                          {t("mealLibrary:recept.carbs")}
                        </div>
                      </div>
                    </div>

                    {r.poznamka && (
                      <p className="text-xs text-muted-foreground italic">
                        {r.poznamka}
                      </p>
                    )}

                    <div className="flex justify-end gap-1 pt-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={t("mealLibrary:recept.viewDetail")}
                      >
                        <Link href={`/recipes/${r.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Link>
                      </Button>

                      {canModifyRecipe(r) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleEdit(r)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(r.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <RecipeFormDialog
        key={editRecipe?.id ?? `create-${formOpen}`}
        open={formOpen}
        onOpenChange={setFormOpen}
        editRecipe={editRecipe}
        potraviny={potraviny}
        allTags={allTags}
        canManageGlobalRecipes={canManageGlobalRecipes}
        canManageTags={canManageTags}
        onSaved={() => utils.recept.list.invalidate()}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteId != null}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("mealLibrary:recept.deleteConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("mealLibrary:recept.deleteConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("mealLibrary:dialog.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
